import { ref } from 'vue'
import { BASE_URL } from "@/composables/url_config"
import { log } from "@/composables/useLog"
import axios from "axios"

const error = ref(null)

// Helper function for headers
const getHeaders = (token) => ({
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json",
    "Accept": "application/json"
})

// ========== Get MVNOs List (for network selection) ==========
export const getMvnosList = async (token) => {
    log("getMvnosList called")
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_mvnos_list_api`
        const result = await axios.get(url, { headers: getHeaders(token) })
        log("getMvnosList result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getMvnosList:")
        log(err)
        return { error: true, message: "Error fetching MVNOs list" }
    }
}

// ========== Add User MSISDN (initiate verification) ==========
export const addUserMsisdn = async (token, msisdnNumber, mvnoId) => {
    log("addUserMsisdn called with:", msisdnNumber, mvnoId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/add_user_msisdn_api`
        const data = {
            msisdn_number: msisdnNumber,
            mvno_id: mvnoId
        }
        const result = await axios.post(url, data, { headers: getHeaders(token) })
        log("addUserMsisdn result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in addUserMsisdn:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error adding MSISDN" }
    }
}

// ========== Send Verification Code ==========
export const sendMsisdnVerificationCode = async (token, msisdnId, method = 'sms') => {
    log("sendMsisdnVerificationCode called for:", msisdnId, method)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/send_msisdn_verification_code_api`
        const data = {
            msisdn_id: msisdnId,
            method: method
        }
        const result = await axios.post(url, data, { headers: getHeaders(token) })
        log("sendMsisdnVerificationCode result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in sendMsisdnVerificationCode:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error sending verification code" }
    }
}

// ========== Verify Code ==========
export const verifyMsisdnCode = async (token, msisdnId, code) => {
    log("verifyMsisdnCode called for:", msisdnId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/verify_msisdn_code_api`
        const data = {
            msisdn_id: msisdnId,
            code: code
        }
        const result = await axios.post(url, data, { headers: getHeaders(token) })
        log("verifyMsisdnCode result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in verifyMsisdnCode:")
        log(err)
        if (err.response?.data?.message) {
            return { error: true, message: err.response.data.message }
        }
        return { error: true, message: "Error verifying code" }
    }
}

// ========== Get User's Own MSISDNs ==========
export const getUserOwnMsisdns = async (token) => {
    log("getUserOwnMsisdns called")
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_user_own_msisdns_api`
        const result = await axios.get(url, { headers: getHeaders(token) })
        log("getUserOwnMsisdns result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getUserOwnMsisdns:")
        log(err)
        return { error: true, message: "Error fetching user MSISDNs", msisdns: [] }
    }
}

// ========== Delete User MSISDN ==========
export const deleteUserMsisdn = async (token, msisdnId) => {
    log("deleteUserMsisdn called for:", msisdnId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/delete_user_msisdn_api`
        const result = await axios.delete(url, {
            headers: getHeaders(token),
            params: { msisdn_id: msisdnId }
        })
        log("deleteUserMsisdn result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in deleteUserMsisdn:")
        log(err)
        return { error: true, message: "Error deleting MSISDN" }
    }
}

// ========== Get Verification Status ==========
export const getMsisdnVerificationStatus = async (token, msisdnId) => {
    log("getMsisdnVerificationStatus called for:", msisdnId)
    if (!token) {
        return { error: true, message: "No token provided" }
    }
    try {
        const url = `${BASE_URL}/home/get_msisdn_verification_status_api`
        const result = await axios.get(url, {
            headers: getHeaders(token),
            params: { msisdn_id: msisdnId }
        })
        log("getMsisdnVerificationStatus result:")
        log(result.data)
        return result.data
    } catch (err) {
        log("Error in getMsisdnVerificationStatus:")
        log(err)
        return { error: true, message: "Error fetching verification status" }
    }
}

const useUserMsisdnsAPI = () => {
    return {
        error,
        getMvnosList,
        addUserMsisdn,
        sendMsisdnVerificationCode,
        verifyMsisdnCode,
        getUserOwnMsisdns,
        deleteUserMsisdn,
        getMsisdnVerificationStatus
    }
}

export default useUserMsisdnsAPI
